import { wallClockNowParam } from './apiDate';

/**
 * THE company wall clock for the SPA (owner decision 2026-09-17).
 *
 * Several screens must decide whether a wall-clock value the user typed is "in the future". The answer
 * has to be judged against the COMPANY's clock, because that is the clock the SERVER judges it against:
 * a tournament kickoff (`Match.ScheduledDateTime`) is a wall-clock contract in the company zone, and
 * `MonthlyPayrollService` refuses a future payroll period against `companyNow`. Judging it against the
 * VIEWER's clock is wrong for anyone travelling, and wrong for every user who simply does not live in
 * the company's zone — it shows a validation error the server would not have raised, or lets through a
 * value the server then 400s.
 *
 * The company zone now rides on the logged-in user's state (`state.user.company.timeZone`, stamped by
 * `AccountService.GetUserInfo` and normalised to IANA by the `Company -> UserCompanyView` map), so no
 * screen needs its own `GET Company/{id}` to answer this.
 *
 * ## The fallback chain is EXPLICIT, and it is not the browser
 *
 * 1. **`company`** — `state.user.company.timeZone`, when `Intl` accepts it. The normal path.
 * 2. **`utc-fallback`** — the zone is missing, or present but `Intl` rejects it (which would mean the
 *    server's read-side normalisation failed to turn a legacy Windows id such as "Arabian Standard Time"
 *    into IANA). UTC is chosen because it is what the SERVER falls back to for exactly these cases —
 *    `TournamentHelpers.ResolveTimeZoneInfoSafe` and `TimeZoneIds.Resolve` both return `TimeZoneInfo.Utc`
 *    on a blank or unrecognised id — so client and server still agree about the clock while degraded.
 *    (Payroll generation does not even reach this rung: the server refuses a company with no zone.)
 * 3. **`browser-fallback`** — last resort, only when this runtime's `Intl` cannot format even `'UTC'`.
 *    It is the wrong answer and it is reported as such: `source` says so and it is warned to the console.
 *    It exists so that "we used the browser's clock" can never happen by accident the way it used to,
 *    when a non-IANA id made `Intl` throw and the helper quietly formatted with the local zone.
 *
 * Never call `wallClockNowParam` with a raw company zone at a call site: its own `catch` falls through to
 * the viewer's clock silently, which is the exact behaviour this module exists to remove. Resolve first.
 */
export type CompanyClockSource = 'company' | 'utc-fallback' | 'browser-fallback';

export interface CompanyClock {
  /** The IANA id actually used. `''` only in the `browser-fallback` case. */
  timeZone: string;
  source: CompanyClockSource;
  /** The raw value from state, kept for diagnostics (may be a Windows id, may be empty). */
  configured: string;
}

/** True when `Intl` can format in this zone — i.e. it is an IANA id this runtime knows. */
export const isUsableTimeZone = (timeZone?: string | null): boolean => {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const warned = new Set<string>();
const warnOnce = (key: string, message: string) => {
  if (warned.has(key)) return;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[companyClock] ${message}`);
};

/**
 * Resolve the company wall clock from the zone carried in logged-in user state.
 * Pure and side-effect-free apart from a one-shot console warning per distinct degraded value.
 */
export const resolveCompanyClock = (companyTimeZone?: string | null): CompanyClock => {
  const configured = (companyTimeZone ?? '').trim();

  if (isUsableTimeZone(configured)) {
    return { timeZone: configured, source: 'company', configured };
  }

  if (isUsableTimeZone('UTC')) {
    if (configured) {
      // A value IS stored but the browser will not take it — almost certainly a Windows id that the
      // server's read-side normalisation did not convert. Worth a shout: it is a data/boundary defect,
      // not a user error.
      warnOnce(
        `unusable:${configured}`,
        `company time zone "${configured}" is not an IANA id this browser accepts; using UTC (the server's own fallback). Expected the server to normalise it — see TimeZoneIds.ToIana.`,
      );
    }
    return { timeZone: 'UTC', source: 'utc-fallback', configured };
  }

  warnOnce(
    'no-intl',
    'Intl cannot format even UTC on this runtime; falling back to the BROWSER clock. Company-relative time checks will be wrong for any viewer outside the company zone.',
  );
  return { timeZone: '', source: 'browser-fallback', configured };
};

/**
 * NOW on the company wall clock as `YYYY-MM-DDTHH:mm` — directly comparable, as a string, with a value
 * out of a `<input type="datetime-local">` / `DateTimeField`.
 */
export const companyWallClockNow = (clock: CompanyClock, now: Date = new Date()): string =>
  // `clock.timeZone` is already known-good (or deliberately empty for the browser rung), so
  // `wallClockNowParam`'s own silent catch is unreachable from here.
  wallClockNowParam(clock.timeZone || undefined, now);

/** The company's current calendar year/month — for period pickers judged against the company clock. */
export const companyNowYearMonth = (clock: CompanyClock, now: Date = new Date()): { year: number; month: number } => {
  const stamp = companyWallClockNow(clock, now);
  return { year: Number(stamp.slice(0, 4)), month: Number(stamp.slice(5, 7)) };
};
